// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white title
// - Page bg #F2F2F2, inner white cards with soft blue border
// - Left orange accent stripe for the Filters/IDs section
// - Inputs/autocomplete/toggles: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, zebra rows, hover tint, subtle borders
// - Checkbox: orange when checked
// - No business logic changed

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  TextField,
  Stack,
  Chip,
  Autocomplete,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
} from "@mui/material";
import { API_URL, API_UPLOAD_URL } from "./config";

const ClassRegistration = () => {
  const token = localStorage.getItem("auth");

  // ===== Theme constants =====
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const TABLE_HEAD = "rgba(25,118,210,0.95)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const dangerOutlinedBtnSx = {
    ...outlinedBtnSx,
    borderColor: "rgba(244,67,54,0.45)",
    color: "rgba(244,67,54,0.95)",
    "&:hover": {
      borderColor: "rgba(244,67,54,0.70)",
      backgroundColor: "rgba(244,67,54,0.06)",
    },
  };

  const toggleGroupSx = {
    borderRadius: 999,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.35)",   // ✅ single outline (only here)
    backgroundColor: "rgba(255,255,255,0.12)",
    p: "3px",                                     // ✅ gives breathing space
    "& .MuiToggleButtonGroup-grouped": {
      border: "0 !important",                     // ✅ removes inner outline
      borderRadius: "999px !important",
      margin: 0,
    },
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 900,
      color: "white",
      border: "0 !important",                     // ✅ IMPORTANT: remove default border
      borderRadius: "999px !important",
      px: 2,
      py: 0.9,
      "&.Mui-selected": {
        backgroundColor: "rgba(255,255,255,0.22) !important",
        color: "white",
      },
      "&:hover": { backgroundColor: "rgba(255,255,255,0.16)" },
    },
    // Optional: separator line between buttons (single clean divider)
    "& .MuiToggleButton-root + .MuiToggleButton-root": {
      position: "relative",
      "&:before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "20%",
        bottom: "20%",
        width: "1px",
        backgroundColor: "rgba(255,255,255,0.28)",
      },
    },
  };


  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const tableWrapSx = { ...surfaceSx, overflow: "hidden" };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ===== auth context =====
  const [campusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [fetchingIDs, setFetchingIDs] = useState(false);

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(payload.campusCode || payload.CampusCode || "");
      setUsername(payload.username || payload.name || "");
    } catch (e) {
      console.error("JWT decode error", e);
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // ===== dropdown lookups =====
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    HostelType: [],
    Section: [],
  });

  // selected filters
  const [subcampusCodes, setSubcampusCodes] = useState([]);
  const [degreeCodes, setDegreeCodes] = useState([]);
  const [branchCodes, setBranchCodes] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [hosteltype, setHostelType] = useState([]);
  const [sections, setSections] = useState([]);

  // ===== Student IDs input (multi) =====
  const [studentIdInput, setStudentIdInput] = useState("");
  const [studentIds, setStudentIds] = useState([]);
  const [studentKeyById, setStudentKeyById] = useState({}); // { [sid]: "SK..." }

  const [filteredStudentIds, setFilteredStudentIds] = useState([]);

  const parseIDs = (txt) =>
    Array.from(
      new Set(
        (txt || "")
          .split(/[\s,]+/g)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );

  const onAddFilters = async () => {
    if (
      subcampusCodes.length === 0 &&
      degreeCodes.length === 0 &&
      branchCodes.length === 0 &&
      semesters.length === 0 &&
      hosteltype.length === 0
    ) {
      setToast({ open: true, type: "info", msg: "Pick at least one filter." });
      return;
    }

    try {
      setFetchingIDs(true);
      const { data } = await axios.post(
        `${API_URL}/api/students/by-filters`,
        {
          SubCampusCodes: subcampusCodes,
          DegreeCodes: degreeCodes,
          BranchCodes: branchCodes,
          Semesters: semesters,
          HostelType: hosteltype,
        },
        authHeader
      );

      const students = Array.isArray(data?.students) ? data.students : [];
      let ids = students.map((s) => String(s.StudentID)).filter(Boolean);

      if (!ids.length) {
        ids = (data?.studentIds || data?.StudentIDs || []).map(String);
      }

      if (!ids.length) {
        setToast({ open: true, type: "info", msg: "No students matched those filters." });
        setStudentIds([]);
        setStudentIdInput("");
        setFilteredStudentIds([]);
        return;
      }

      setStudentIds(ids);
      setStudentIdInput(ids.join(", "));
      setFilteredStudentIds(ids);

      if (students.length) {
        setStudentKeyById((prev) => {
          const next = { ...prev };
          for (const s of students) {
            const sid = String(s.StudentID || "").trim();
            const sk = String(s.StudentKey || "").trim();
            if (sid && sk) next[sid] = sk;
          }
          return next;
        });
      } else {
        await ensureKeysForCurrentIDs();
      }

      setToast({ open: true, type: "success", msg: `Loaded ${ids.length} StudentID(s) from filters.` });
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed to fetch StudentIDs." });
    } finally {
      setFetchingIDs(false);
    }
  };

  const ensureKeysForCurrentIDs = async () => {
    const missing = studentIds.filter((sid) => !studentKeyById[sid]);
    if (missing.length === 0) return;

    try {
      const { data } = await axios.post(
        `${API_URL}/api/students/resolvestudentkeys`,
        { StudentIDs: missing },
        authHeader
      );
      const pairs = Array.isArray(data?.students) ? data.students : [];
      if (!pairs.length) {
        setToast({ open: true, type: "warning", msg: `No StudentKey found for ${missing.length} ID(s).` });
        return;
      }
      setStudentKeyById((prev) => {
        const next = { ...prev };
        for (const p of pairs) {
          const sid = String(p.StudentID || "").trim();
          const sk = String(p.StudentKey || "").trim();
          if (sid && sk) next[sid] = sk;
        }
        return next;
      });
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed to resolve StudentKeys." });
    }
  };

  // ===== grid state =====
  const [availableByStudent, setAvailableByStudent] = useState({});
  const [registeredByStudent, setRegisteredByStudent] = useState({});
  const [registeredSubjectKeysByStudent, setRegisteredSubjectKeysByStudent] = useState({});
  const [allCourses, setAllCourses] = useState([]);

  const [selectedAdd, setSelectedAdd] = useState({});
  const [selectedDrop, setSelectedDrop] = useState({});

  const [mode, setMode] = useState("add"); // 'add' | 'drop'

  const [loadingGrid, setLoadingGrid] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  // ✅ NEW: reset helpers
  const clearGrid = () => {
    setLoadingGrid(false);
    setAvailableByStudent({});
    setRegisteredByStudent({});
    setRegisteredSubjectKeysByStudent({});
    setAllCourses([]);
    setSelectedAdd({});
    setSelectedDrop({});
    // 2) clear student id search
    setStudentIdInput("");
    setStudentIds([]);
    setFilteredStudentIds([]); // important: reset tracker too
    
    // 3) clear section filter (ONLY)
    setSections([]);
    
    // (optional) if you want to clear resolved keys also, uncomment:
    // setStudentKeyById({});
    
    setToast({ open: true, type: "info", msg: "Grid + StudentIDs + Sections cleared." });
    };

  /*
  const clearFiltersOnly = () => {
    setSubcampusCodes([]);
    setDegreeCodes([]);
    setBranchCodes([]);
    setSemesters([]);
    setHostelType([]);
    setToast({ open: true, type: "info", msg: "Filters cleared." });
  };
  */

  const clearFiltersOnly = () => {
    // 1) clear dropdown selections
    setSubcampusCodes([]);
    setDegreeCodes([]);
    setBranchCodes([]);
    setSemesters([]);
    setHostelType([]);
    setSections([]);

    // 2) remove ONLY the IDs that came from filters
    setStudentIds((prev) => {
      if (!filteredStudentIds.length) return prev; // nothing to remove
      const filteredSet = new Set(filteredStudentIds);
      const kept = prev.filter((sid) => !filteredSet.has(sid));
      setStudentIdInput(kept.join(", "));
      return kept;
    });

    // 3) clear the "filtered IDs" tracker itself
    setFilteredStudentIds([]);

    // ✅ recommended: also clear grid so UI doesn't show stale courses for removed IDs
    clearGrid();

    setToast({ open: true, type: "info", msg: "Filters + filtered StudentIDs cleared." });
  };


  const hasAnyFilter =
    studentIds.length > 0 ||
    subcampusCodes.length > 0 ||
    degreeCodes.length > 0 ||
    branchCodes.length > 0 ||
    semesters.length > 0 ||
    hosteltype.length > 0 ||
    sections.length > 0;

  // ===== lookups loader =====
  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
          HostelType: (data?.HostelType || []).map(String),
          Section: (data?.Section || data?.Sections || []).map(String),
        });
        
      } catch (e) {
        console.error(e);
        setToast({ open: true, type: "error", msg: "Failed to load dropdowns." });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeader]);

  // ===== dropdown renderer =====
  const renderMultiDropdown = (label, value, setValue, optionsList) => (
    <Autocomplete
      multiple
      options={optionsList}
      value={value}
      onChange={(_, newVal) => setValue(newVal.map((v) => String(v).trim()).filter(Boolean))}
      disableCloseOnSelect
      filterSelectedOptions
      openOnFocus
      loading={loadingLookups}
      sx={{ width: "100%" }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          fullWidth
          sx={inputSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingLookups ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  // Normalizer to build a stable key per subject
  const normalizeName = (name) => (name || "").trim().toLowerCase();
  const subjectKeyFrom = (obj) =>
    String(obj?.SubjectCode ?? obj?.SubjectNameCode ?? normalizeName(obj?.CourseName));

  // ===== Load Grid (on click) =====
  const loadGrid = async () => {
    if (!hasAnyFilter) {
      setToast({ open: true, type: "info", msg: "Add at least one filter (StudentIDs or codes)." });
      return;
    }
    if (studentIds.length === 0) {
      setToast({ open: true, type: "info", msg: "Enter at least one StudentID." });
      return;
    }

    await ensureKeysForCurrentIDs();

    setLoadingGrid(true);
    setAvailableByStudent({});
    setRegisteredByStudent({});
    setRegisteredSubjectKeysByStudent({});
    setAllCourses([]);
    setSelectedAdd({});
    setSelectedDrop({});

    try {
      const results = await Promise.all(
        studentIds.map(async (sid) => {
          const [availResp, regResp] = await Promise.all([
            axios.get(`${API_URL}/api/students/available-courses`, {
              ...authHeader,
              params: {
                StudentID: sid,
                ...(sections.length ? { Sections: sections } : {}),
              },
            }),
            axios.get(`${API_URL}/api/students/registered-courses`, { ...authHeader, params: { StudentID: sid } }),
          ]);
          const avail = Array.isArray(availResp.data) ? availResp.data : [];
          const regs = Array.isArray(regResp.data) ? regResp.data : [];
          return [sid, avail, regs];
        })
      );

      const availMap = {};
      const regMap = {};
      const regSubjMap = {};
      const unionById = new Map();

      for (const [sid, avail, regs] of results) {
        availMap[sid] = avail;
        for (const c of avail) {

          const cid = String(c.CourseID);
          if (!unionById.has(cid)) {
            unionById.set(cid, {
              CourseID: cid,
              CourseName: c.CourseName || c.SubjectNameCode || c.SubjectName || "",
              Section: c.Section || "",
              Semester: c.Semester || "",
              Credits: c.Credits || "",
              SubjectKey: subjectKeyFrom(c),
            });
          }
        }

        const regSet = new Set();
        const subjSet = new Set();
        for (const r of regs) {

          const cid = String(r.CourseID);
          regSet.add(cid);
          subjSet.add(subjectKeyFrom(r));

          if (!unionById.has(cid)) {
            unionById.set(cid, {
              CourseID: cid,
              CourseName: r.CourseName || r.SubjectNameCode || r.SubjectName || "",
              Section: r.Section || "",
              Semester: r.Semester || "",
              Credits: r.Credits || "",
              SubjectKey: subjectKeyFrom(r),
            });
          }
        }
        regMap[sid] = regSet;
        regSubjMap[sid] = subjSet;
      }

      const cols = Array.from(unionById.values()).sort((a, b) => {
        const sa = String(a.Semester || "");
        const sb = String(b.Semester || "");
        if (sa !== sb) return sa.localeCompare(sb, undefined, { numeric: true });
        const na = (a.CourseName || "").toLowerCase();
        const nb = (b.CourseName || "").toLowerCase();
        if (na !== nb) return na.localeCompare(nb);
        return String(a.CourseID).localeCompare(String(b.CourseID), undefined, { numeric: true });
      });

      const initAdd = {};
      const initDrop = {};
      for (const sid of studentIds) {
        initAdd[sid] = new Set();
        initDrop[sid] = new Set();
      }

      setAvailableByStudent(availMap);
      setRegisteredByStudent(regMap);
      setRegisteredSubjectKeysByStudent(regSubjMap);
      setAllCourses(cols);
      setSelectedAdd(initAdd);
      setSelectedDrop(initDrop);
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed to load grid." });
    } finally {
      setLoadingGrid(false);
    }
  };

  const courseById = useMemo(() => {
    const m = new Map();
    for (const c of allCourses) m.set(String(c.CourseID), c);
    return m;
  }, [allCourses]);

  const subjectKeyOf = (courseId) => courseById.get(String(courseId))?.SubjectKey ?? "";

  const isRegistered = (sid, courseId) => (registeredByStudent[sid] || new Set()).has(String(courseId));

  const getAvailableCourseForStudent = (sid, courseId) =>
    (availableByStudent[sid] || []).find(
      (c) => String(c.CourseID) === String(courseId)
    );

  const isAvailable = (sid, courseId) => {
    const row = getAvailableCourseForStudent(sid, courseId);
    if (!row) return false;
    return Number(row.CanRegister ?? 1) === 1;
  };

  const isPreassignedSectionMatch = (sid, courseId) => {
    const row = getAvailableCourseForStudent(sid, courseId);
    if (!row) return false;
    return Number(row.IsPreassignedSectionMatch ?? 0) === 1;
  };

  const getStudentSection = (sid, courseId) => {
    const row = getAvailableCourseForStudent(sid, courseId);
    return String(row?.StudentSection || "").trim();
  };

  const toggleCell = (sid, courseId) => {
    if (mode === "add") {
      if (isRegistered(sid, courseId)) return;
      if (!isAvailable(sid, courseId)) return;

      const subjKey = subjectKeyOf(courseId);
      if ((registeredSubjectKeysByStudent[sid] || new Set()).has(subjKey)) return;

      setSelectedAdd((prev) => {
        const next = { ...prev };
        const s = new Set(next[sid] || []);
        if (s.has(courseId)) {
          s.delete(courseId);
        } else {
          for (const otherId of Array.from(s)) {
            if (subjectKeyOf(otherId) === subjKey && String(otherId) !== String(courseId)) s.delete(otherId);
          }
          s.add(String(courseId));
        }
        next[sid] = s;
        return next;
      });
    } else {
      if (!isRegistered(sid, courseId)) return;
      setSelectedDrop((prev) => {
        const next = { ...prev };
        const s = new Set(next[sid] || []);
        if (s.has(courseId)) s.delete(courseId);
        else s.add(String(courseId));
        next[sid] = s;
        return next;
      });
    }
  };

  const rowSelectAll = (sid, checked) => {
    if (mode === "add") {
      setSelectedAdd((prev) => {
        const next = { ...prev };
        if (!checked) {
          next[sid] = new Set();
          return next;
        }
        const already = registeredSubjectKeysByStudent[sid] || new Set();
        const seen = new Set();
        const s = new Set();
        for (const c of allCourses) {
          const cid = String(c.CourseID);
          const subjKey = c.SubjectKey || "";
          if (!isAvailable(sid, cid)) continue;
          if (already.has(subjKey)) continue;
          if (seen.has(subjKey)) continue;
          seen.add(subjKey);
          s.add(cid);
        }
        next[sid] = s;
        return next;
      });
    } else {
      setSelectedDrop((prev) => {
        const next = { ...prev };
        if (!checked) {
          next[sid] = new Set();
          return next;
        }
        const s = new Set();
        for (const c of allCourses) {
          const cid = String(c.CourseID);
          if (isRegistered(sid, cid)) s.add(cid);
        }
        next[sid] = s;
        return next;
      });
    }
  };

  const anySelected = () => {
    const pool = mode === "add" ? selectedAdd : selectedDrop;
    return Object.values(pool).some((set) => set && set.size > 0);
  };

  const globalSelectAll = (checked) => {
    if (mode === "add") {
      setSelectedAdd((prev) => {
        const next = { ...prev };
        for (const sid of studentIds) {
          if (!checked) {
            next[sid] = new Set();
            continue;
          }
          const already = registeredSubjectKeysByStudent[sid] || new Set();
          const seen = new Set();
          const s = new Set();
          for (const c of allCourses) {
            const cid = String(c.CourseID);
            const subjKey = c.SubjectKey || "";
            if (!isAvailable(sid, cid)) continue;
            if (already.has(subjKey)) continue;
            if (seen.has(subjKey)) continue;
            seen.add(subjKey);
            s.add(cid);
          }
          next[sid] = s;
        }
        return next;
      });
    } else {
      setSelectedDrop((prev) => {
        const next = { ...prev };
        for (const sid of studentIds) {
          if (!checked) {
            next[sid] = new Set();
            continue;
          }
          const s = new Set();
          for (const c of allCourses) {
            const cid = String(c.CourseID);
            if (isRegistered(sid, cid)) s.add(cid);
          }
          next[sid] = s;
        }
        return next;
      });
    }
  };

  const headerStateForCourse = (course) => {
    const cid = String(course.CourseID);
    const subjKey = course.SubjectKey || "";
    let total = 0;
    let selectedCount = 0;

    for (const sid of studentIds) {
      if (mode === "add") {
        if (!isAvailable(sid, cid)) continue;
        if ((registeredSubjectKeysByStudent[sid] || new Set()).has(subjKey)) continue;
        total++;
        if ((selectedAdd[sid] || new Set()).has(cid)) selectedCount++;
      } else {
        if (!isRegistered(sid, cid)) continue;
        total++;
        if ((selectedDrop[sid] || new Set()).has(cid)) selectedCount++;
      }
    }

    return {
      total,
      selectedCount,
      checked: total > 0 && selectedCount === total,
      indeterminate: selectedCount > 0 && selectedCount < total,
      disabled: total === 0,
    };
  };

  const toggleColumnForAll = (course, checked) => {
    const cid = String(course.CourseID);
    const subjKey = course.SubjectKey || "";

    if (mode === "add") {
      setSelectedAdd((prev) => {
        const next = { ...prev };
        for (const sid of studentIds) {
          if (!isAvailable(sid, cid)) continue;
          if ((registeredSubjectKeysByStudent[sid] || new Set()).has(subjKey)) continue;
          const set = new Set(next[sid] || []);
          if (checked) {
            for (const otherId of Array.from(set)) {
              if (subjectKeyOf(otherId) === subjKey && otherId !== cid) set.delete(otherId);
            }
            set.add(cid);
          } else {
            set.delete(cid);
          }
          next[sid] = set;
        }
        return next;
      });
    } else {
      setSelectedDrop((prev) => {
        const next = { ...prev };
        for (const sid of studentIds) {
          if (!isRegistered(sid, cid)) continue;
          const set = new Set(next[sid] || []);
          if (checked) set.add(cid);
          else set.delete(cid);
          next[sid] = set;
        }
        return next;
      });
    }
  };

  const onRegister = async () => {
    const items = [];
    const noKey = [];

    for (const sid of studentIds) {
      const sk = studentKeyById[sid];
      if (!sk) {
        noKey.push(sid);
        continue;
      }

      for (const cid of selectedAdd[sid] || []) {
        if (!isRegistered(sid, cid)) items.push({ StudentKey: sk, CourseID: String(cid) });
      }
    }

    if (noKey.length) setToast({ open: true, type: "warning", msg: `Missing StudentKey for ${noKey.length} ID(s). Skipped.` });

    if (items.length === 0) {
      setToast({ open: true, type: "info", msg: "No selections to register." });
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/api/students/register-courses`,
        {
          items,
          SectionOverride: sections.length > 0,
          SelectedSections: sections,
        },
        authHeader
      );
      const msg = data?.ok
        ? `Requested ${data.requested ?? items.length}. Inserted ${data.inserted ?? "?"}${typeof data.duplicates === "number" ? `, duplicates ignored ${data.duplicates}` : ""}.`
        : `Registered ${items.length} selection(s).`;
      setToast({ open: true, type: "success", msg });
      await loadGrid();
      setSelectedAdd({});
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        type: "error",
        msg: e?.response?.data?.error || "Registration failed for one or more items.",
      });
    }
  };

  const onDropSelected = async () => {
    const items = [];
    const noKey = [];
    for (const sid of studentIds) {
      const sk = studentKeyById[sid];
      if (!sk) {
        noKey.push(sid);
        continue;
      }
      for (const cid of selectedDrop[sid] || []) {
        if (isRegistered(sid, cid)) items.push({ StudentKey: sk, CourseID: cid });
      }
    }

    if (noKey.length) setToast({ open: true, type: "warning", msg: `Missing StudentKey for ${noKey.length} ID(s). Skipped.` });

    if (items.length === 0) {
      setToast({ open: true, type: "info", msg: "No selections to drop." });
      return;
    }

    if (!window.confirm(`Drop ${items.length} course(s) across ${studentIds.length} student(s)?`)) return;

    try {
      const { data } = await axios.post(`${API_URL}/api/students/drop-courses`, { items }, authHeader);
      const msg = data?.ok
        ? `Dropped ${data.dropped ?? 0} of ${data.requested ?? items.length} selection(s)${typeof data.notFound === "number" && data.notFound > 0 ? ` (${data.notFound} not found)` : ""}.`
        : "Drop completed.";
      setToast({ open: true, type: "success", msg });
      await loadGrid();
      setSelectedDrop({});
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: e?.response?.data?.error || "Bulk drop failed." });
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Class Registration Management
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Register/Drop courses in bulk using a grid
            </Typography>
          </Box>

          {/* Mode switch */}
          <ToggleButtonGroup
            size="small"
            value={mode}
            exclusive
            onChange={(_, val) => val && setMode(val)}
            sx={toggleGroupSx}
          >
            <ToggleButton value="add">Register mode</ToggleButton>
            <ToggleButton value="drop">Drop mode</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters block with orange accent stripe */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters & Student IDs
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              {/* Row 1: filters */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(7, 1fr)" },
                  gap: 1,
                  mb: 1.5,
                }}
              >
                {renderMultiDropdown("SubCampusCode", subcampusCodes, setSubcampusCodes, options.SubCampusCode)}
                {renderMultiDropdown("DegreeCode", degreeCodes, setDegreeCodes, options.DegreeCode)}
                {renderMultiDropdown("BranchCode", branchCodes, setBranchCodes, options.BranchCode)}
                {renderMultiDropdown("Semester", semesters, setSemesters, options.SemesterCode)}
                {renderMultiDropdown("HostelType", hosteltype, setHostelType, options.HostelType)}
                {/*{renderMultiDropdown("Section", sections, setSections, options.Section)}*/}

                <Button
                  size="small"
                  variant="outlined"
                  onClick={onAddFilters}
                  disabled={fetchingIDs}
                  sx={{ ...outlinedBtnSx, alignSelf: "flex-start", height: 36, minWidth: 120 }}
                >
                  {fetchingIDs ? "Fetching IDs…" : "Add filters"}
                </Button>
                {/* ✅ NEW: Clear Filters */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearFiltersOnly}
                  disabled={
                    fetchingIDs ||
                    (subcampusCodes.length === 0 &&
                      degreeCodes.length === 0 &&
                      branchCodes.length === 0 &&
                      semesters.length === 0 &&
                      hosteltype.length === 0 &&
                      filteredStudentIds.length === 0)
                  }
                  sx={{ ...dangerOutlinedBtnSx, alignSelf: "flex-start", height: 36, minWidth: 120 }}
                >
                  Clear Filters
                </Button>
              </Box>

              {/* Row 2: IDs + actions */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                sx={{ mb: 0.5, alignItems: "flex-start" }}
              >
                {/* Student IDs + optional manual Section override */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 320,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                    gap: 1,
                    alignItems: "start",
                  }}
                >
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Student IDs (comma / space / newline)"
                      placeholder="e.g. STU001, STU002"
                      value={studentIdInput}
                      onChange={(e) => {
                        const txt = e.target.value;
                        setStudentIdInput(txt);
                        setStudentIds(parseIDs(txt));
                        setFilteredStudentIds([]);
                      }}
                      sx={inputSx}
                    />

                    {studentIds.length > 0 && (
                      <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                        {studentIds.map((id) => (
                          <Chip
                            key={id}
                            size="small"
                            label={id}
                            onDelete={() => {
                              const next = studentIds.filter((x) => x !== id);
                              setStudentIds(next);
                              setStudentIdInput(next.join(", "));
                              setFilteredStudentIds((prev) => prev.filter((x) => x !== id));
                            }}
                            sx={{
                              mb: 1,
                              borderRadius: 2,
                              border: `1px solid ${BORDER}`,
                              backgroundColor: "rgba(25,118,210,0.04)",
                              "& .MuiChip-deleteIcon": { color: "rgba(25,118,210,0.75)" },
                            }}
                          />
                        ))}
                      </Stack>
                    )}

                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.75, color: "rgba(0,0,0,0.68)" }}
                    >
                      Leave Section blank to use each student's preassigned Section automatically.
                      Matching class cells are highlighted light orange. Select one or more Sections
                      to manually show/register only those sections.
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: { xs: "100%", md: 150 } }}>
                    {renderMultiDropdown(
                      "Section",
                      sections,
                      setSections,
                      options.Section
                    )}
                    {sections.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: THEME_ORANGE,
                          fontWeight: 800,
                        }}
                      >
                        Manual section override active
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Button
                  size="small"
                  variant="contained"
                  onClick={loadGrid}
                  disabled={loadingGrid || studentIds.length === 0}
                  sx={{ ...primaryBtnSx, alignSelf: "flex-start", height: 36, minWidth: 110 }}
                >
                  {loadingGrid ? "Loading…" : "Load Grid"}
                </Button>
                  
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearGrid}
                  disabled={loadingGrid || (allCourses.length === 0 && studentIds.length === 0 && sections.length === 0)}
                  sx={{ ...outlinedBtnSx, alignSelf: "flex-start", height: 36, minWidth: 110 }}
                >
                  Clear Grid
                </Button>
                  
                <Tooltip
                  title={
                    mode === "add"
                      ? "Select/Deselect one eligible class per subject. With no Section override, preassigned student sections are enforced automatically."
                      : "Select/Deselect all registered courses for all listed students"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => globalSelectAll(!anySelected())}
                      disabled={loadingGrid || allCourses.length === 0 || studentIds.length === 0}
                      sx={{ ...outlinedBtnSx, alignSelf: "flex-start", height: 36, minWidth: 120 }}
                    >
                      {anySelected() ? "Clear All" : "Select All"}
                    </Button>
                  </span>
                </Tooltip>
                
                <Button
                  size="small"
                  variant="contained"
                  onClick={onRegister}
                  disabled={mode !== "add" || !anySelected()}
                  sx={{ ...primaryBtnSx, alignSelf: "flex-start", height: 36, minWidth: 110 }}
                >
                  Register
                </Button>
                
                <Button
                  size="small"
                  variant="contained"
                  onClick={onDropSelected}
                  disabled={mode !== "drop" || !anySelected()}
                  sx={{
                    ...primaryBtnSx,
                    alignSelf: "flex-start",
                    height: 36,
                    minWidth: 130,
                    backgroundColor: "rgba(244,67,54,0.92)",
                    "&:hover": { backgroundColor: "rgba(244,67,54,1)" },
                  }}
                >
                  Drop Selected
                </Button>
              </Stack>

              {/*
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                sx={{ mb: 0.5, alignItems: "flex-start" }}
              >
                <Box sx={{ flex: 1, minWidth: 320 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Student IDs (comma / space / newline)"
                    placeholder="e.g. STU001, STU002"
                    value={studentIdInput}
                    onChange={(e) => {
                      const txt = e.target.value;
                      setStudentIdInput(txt);
                      setStudentIds(parseIDs(txt));
                      setFilteredStudentIds([]);
                    }}
                    sx={inputSx}
                  />

                  {studentIds.length > 0 && (
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                      {studentIds.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={id}
                          onDelete={() => {
                            const next = studentIds.filter((x) => x !== id);
                            setStudentIds(next);
                            setStudentIdInput(next.join(", "));
                            setFilteredStudentIds((prev) => prev.filter((x) => x !== id));
                          }}
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            border: `1px solid ${BORDER}`,
                            backgroundColor: "rgba(25,118,210,0.04)",
                            "& .MuiChip-deleteIcon": { color: "rgba(25,118,210,0.75)" },
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Button
                  size="small"
                  variant="contained"
                  onClick={loadGrid}
                  disabled={loadingGrid || studentIds.length === 0}
                  sx={{ ...primaryBtnSx, alignSelf: "flex-start", height: 36, minWidth: 110 }}
                >
                  {loadingGrid ? "Loading…" : "Load Grid"}
                </Button>

                
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearGrid}
                  disabled={loadingGrid || allCourses.length === 0}
                  sx={{ ...outlinedBtnSx, alignSelf: "flex-start", height: 36, minWidth: 110 }}
                >
                  Clear Grid
                </Button>

                <Tooltip
                  title={
                    mode === "add"
                      ? "Select/Deselect one eligible class per subject. With no Section override, preassigned student sections are enforced automatically."
                      : "Select/Deselect all registered courses for all listed students"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => globalSelectAll(!anySelected())}
                      disabled={loadingGrid || allCourses.length === 0 || studentIds.length === 0}
                      sx={{ ...outlinedBtnSx, alignSelf: "flex-start", height: 36, minWidth: 120 }}
                    >
                      {anySelected() ? "Clear All" : "Select All"}
                    </Button>
                  </span>
                </Tooltip>

                <Button
                  size="small"
                  variant="contained"
                  onClick={onRegister}
                  disabled={mode !== "add" || !anySelected()}
                  sx={{ ...primaryBtnSx, alignSelf: "flex-start", height: 36, minWidth: 110 }}
                >
                  Register
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  onClick={onDropSelected}
                  disabled={mode !== "drop" || !anySelected()}
                  sx={{
                    ...primaryBtnSx,
                    alignSelf: "flex-start",
                    height: 36,
                    minWidth: 130,
                    backgroundColor: "rgba(244,67,54,0.92)",
                    "&:hover": { backgroundColor: "rgba(244,67,54,1)" },
                  }}
                >
                  Drop Selected
                </Button>
              </Stack>
              */}
            </Box>
          </Paper>

          <Divider sx={{ mb: 2, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* ✅ Grid table */}
          {/* OUTER – keeps rounded corners & spacing */}
          <Paper elevation={0} sx={tableWrapSx}>

            {/* INNER – ONLY for horizontal scroll */}
            <Box
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >          
          <TableContainer component="div">
            <Table
              size="small"
              sx={{
                "& thead .MuiTableCell-root": { py: 0.75, px: 0.75, fontSize: 12 },
                "& tbody .MuiTableCell-root": { py: 0.5, px: 0.75, fontSize: 12 },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: TABLE_HEAD }}>
                  <TableCell
                    sx={{
                      color: "#fff",
                      position: "sticky",
                      left: 0,
                      background: TABLE_HEAD,
                      zIndex: 2,
                      fontWeight: 900,
                    }}
                  >
                    StudentID
                  </TableCell>

                  {allCourses.map((c) => {
                    const { checked, indeterminate, disabled } = headerStateForCourse(c);
                    return (
                      <TableCell key={c.CourseID} sx={{ color: "#fff", whiteSpace: "nowrap" }} align="center">
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <span style={{ fontWeight: 900 }}>{c.CourseName || c.CourseID}</span>
                          <span style={{ fontSize: 11, opacity: 0.9 }}>
                            {c.Section ? `Sec ${c.Section}` : ""}
                          </span>

                          <Tooltip
                            title={
                              mode === "add"
                                ? "Select this section for all eligible students (one per subject)"
                                : "Select this registered section for all students who have it"
                            }
                          >
                            <span>
                              <Checkbox
                                size="small"
                                sx={{ ...checkboxSx, color: "rgba(255,255,255,0.9)", p: 0.25, mt: 0.25 }}
                                disabled={disabled}
                                indeterminate={indeterminate}
                                checked={checked}
                                onChange={(e) => toggleColumnForAll(c, e.target.checked)}
                              />
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    );
                  })}

                  <TableCell sx={{ color: "#fff", fontWeight: 900 }} align="center">
                    Row Select
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {studentIds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={1 + allCourses.length + 1}>
                      <Typography sx={{ p: 2, color: "rgba(0,0,0,0.7)" }}>
                        Add StudentIDs and click “Load Grid”.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : loadingGrid ? (
                  <TableRow>
                    <TableCell colSpan={1 + allCourses.length + 1}>
                      <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}>
                        <CircularProgress size={20} />
                        <Typography>Loading grid…</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  studentIds.map((sid, i) => {
                    const addSet = selectedAdd[sid] || new Set();
                    const dropSet = selectedDrop[sid] || new Set();

                    const totalEligible =
                      mode === "add"
                        ? allCourses
                            .filter((c) => {
                              const cid = String(c.CourseID);
                              const subjKey = c.SubjectKey || "";
                              return (
                                isAvailable(sid, cid) &&
                                !(registeredSubjectKeysByStudent[sid] || new Set()).has(subjKey)
                              );
                            })
                            .reduce((acc, c) => {
                              acc.add(c.SubjectKey || "");
                              return acc;
                            }, new Set()).size
                        : allCourses.filter((c) => isRegistered(sid, c.CourseID)).length;

                    const selectedCount = mode === "add" ? Array.from(addSet).length : Array.from(dropSet).length;
                    const rowAllChecked = totalEligible > 0 && selectedCount === totalEligible;
                    const rowIndeterminate = selectedCount > 0 && selectedCount < totalEligible;

                    return (
                      <TableRow key={`${sid}-${i}`} sx={zebraRowSx(i)}>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            background: i % 2 === 0 ? ROW_ALT : "#FFFFFF",
                            zIndex: 1,
                            fontWeight: 900,
                            color: "rgba(25,118,210,0.95)",
                          }}
                        >
                          {sid}
                        </TableCell>

                        {allCourses.map((c) => {
                          const cid = String(c.CourseID);
                          const reg = isRegistered(sid, cid);
                          const canTake = isAvailable(sid, cid);
                          const preassignedMatch = isPreassignedSectionMatch(sid, cid);
                          const studentSection = getStudentSection(sid, cid);
                          const subjKey = c.SubjectKey || "";

                          if (mode === "add") {
                            const subjectAlreadyRegistered = (registeredSubjectKeysByStudent[sid] || new Set()).has(subjKey);

                            const hasSelectedSameSubject = Array.from(addSet).some(
                              (selId) => selId !== cid && subjectKeyOf(selId) === subjKey
                            );

                            const disabled = reg || !canTake || subjectAlreadyRegistered || hasSelectedSameSubject;
                            const checked = reg ? true : addSet.has(cid);

                            const tooltip = subjectAlreadyRegistered
                              ? "This subject is already registered (another section)"
                              : hasSelectedSameSubject
                              ? "Another section of this subject is selected"
                              : !canTake && preassignedMatch
                              ? `Preassigned Section ${studentSection}, but currently unavailable`
                              : !canTake
                              ? sections.length > 0
                                ? "Not available under the selected Section override"
                                : studentSection
                                ? `Student is preassigned to Section ${studentSection} for this subject`
                                : "Not available for this student"
                              : reg
                              ? "Already registered"
                              : preassignedMatch
                              ? `Preassigned Section ${studentSection} — recommended/automatic match`
                              : sections.length > 0
                              ? "Available through manual Section override"
                              : "";

                            return (
                              <TableCell
                                key={`${sid}-${cid}`}
                                align="center"
                                sx={{
                                  ...(preassignedMatch
                                    ? {
                                        backgroundColor: "rgba(240,120,40,0.13)",
                                        boxShadow: "inset 0 0 0 1px rgba(240,120,40,0.18)",
                                      }
                                    : {}),
                                }}
                              >
                                <Tooltip title={tooltip}>
                                  <span>
                                    <Checkbox
                                      size="small"
                                      disabled={disabled}
                                      checked={checked}
                                      onChange={() => toggleCell(sid, cid)}
                                      sx={{
                                        ...checkboxSx,
                                        p: 0.25,
                                        ...(preassignedMatch
                                          ? {
                                              color: "rgba(240,120,40,0.55)",
                                              "&.Mui-checked": {
                                                color: THEME_ORANGE,
                                              },
                                              "&.Mui-disabled": {
                                                color: "rgba(240,120,40,0.32)",
                                              },
                                            }
                                          : {}),
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </TableCell>
                            );
                          } else {
                            const isRegisteredCell = reg;
                            const isSelectedToDrop = dropSet.has(cid);
                            const disabled = !isRegisteredCell;
                            const tooltip = !isRegisteredCell
                              ? "Not registered"
                              : isSelectedToDrop
                              ? "Selected to drop"
                              : "Registered";

                            return (
                              <TableCell key={`${sid}-${cid}`} align="center">
                                <Tooltip title={tooltip}>
                                  <span>
                                    <Checkbox
                                      size="small"
                                      disabled={disabled}
                                      checked={isRegisteredCell}
                                      {...(isSelectedToDrop ? { color: "error" } : {})}
                                      onChange={() => toggleCell(sid, cid)}
                                      sx={{
                                        p: 0.25,
                                        ...(isSelectedToDrop
                                          ? { "&.Mui-checked": { color: "rgba(244,67,54,0.95)" } }
                                          : checkboxSx),
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </TableCell>
                            );
                          }
                        })}

                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            indeterminate={rowIndeterminate}
                            checked={rowAllChecked && totalEligible > 0}
                            onChange={(e) => rowSelectAll(sid, e.target.checked)}
                            sx={{ ...checkboxSx, p: 0.25 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast({ ...toast, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : toast.type === "warning" ? "warning" : "success"}>
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
};

export default ClassRegistration;
